import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { isGroupHost } from './utils/dropzones.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { SystemConfigProvider } from './contexts/SystemConfigContext';
import { useAuth } from '@/contexts/AuthContext'

/**
 * `/app/dashboard` on the GROUP host. An owner there is the Super Admin and
 * the group host administers no field of its own — its per-field dashboard
 * would describe the `skydivingos` database, a dropzone nobody flies at. So
 * owners land on the Super Admin console; everyone else (and every other
 * host) gets the ordinary dashboard, unchanged.
 */
const DashboardOrSuperAdmin = ({ children }) => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner' || (user?.staffRoles || []).includes('owner');
  if (isGroupHost() && isOwner) return <Navigate to="/app/group" replace />;
  return children;
};
// ─── Eager imports — kept in the main bundle because they are on the critical
// path for first paint or are tiny wrapper components. Everything else below
// is lazy-loaded per-route so the initial JS bundle stays small.
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import AppLayout from './layouts/AppLayout';
import KioskLayout from './layouts/KioskLayout';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerRoute from './components/CustomerRoute';
import PublicRoute from './components/PublicRoute';
import FunJumperPage from './pages/app/FunJumperPage';

// ─── Public / landing pages (lazy — not everyone visits every entry point) ──
const BookingPage              = lazy(() => import('./pages/BookingPage'));
const BookingSuccessPage       = lazy(() => import('./pages/BookingSuccessPage'));
const SignupPage               = lazy(() => import('./pages/SignupPage'));
const ForgotPasswordPage       = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage        = lazy(() => import('./pages/ResetPasswordPage'));
const SetPasswordPage          = lazy(() => import('./pages/SetPasswordPage'));
const SsoHandoffPage           = lazy(() => import('./pages/SsoHandoffPage'));
const CustomerLandingPage      = lazy(() => import('./pages/CustomerLandingPage'));
const SigningPage              = lazy(() => import('./pages/SigningPage'));
const MediaViewPage            = lazy(() => import('./pages/MediaViewPage'));
const CustomerGalleryPage      = lazy(() => import('./pages/CustomerGalleryPage'));
const MediaUnlockPage          = lazy(() => import('./pages/MediaUnlockPage'));
const GroupDetailsPage         = lazy(() => import('./pages/GroupDetailsPage'));
const GroupBookingDetailsPage  = lazy(() => import('./pages/public/GroupBookingDetailsPage'));
const GroupBookingSuccessPage  = lazy(() => import('./pages/GroupBookingSuccessPage'));
const LiveBoardPage            = lazy(() => import('./pages/public/LiveBoardPage'));

// ─── Kiosk flow (only loaded when /kiosk/* routes are hit) ──────────────────
const KioskLoginPage           = lazy(() => import('./pages/KioskLoginPage'));
const KioskScannerPage         = lazy(() => import('./pages/KioskScannerPage'));
// React.lazy exposes a single default export per call, so the two named
// kiosk payment result components are imported via two separate lazy calls.
// Both land in the same JS chunk because webpack de-dupes the shared module.
const KioskPaymentSuccess      = lazy(() => import('./pages/KioskPaymentResultPage').then((m) => ({ default: m.KioskPaymentSuccess })));
const KioskPaymentCancelled   = lazy(() => import('./pages/KioskPaymentResultPage').then((m) => ({ default: m.KioskPaymentCancelled })));

// ─── Mobile waiver (opened from kiosk QR) ───────────────────────────────────
const WaiverMobilePage         = lazy(() => import('./pages/WaiverMobilePage'));
const WaiverGuestPage          = lazy(() => import('./pages/WaiverGuestPage'));

// ─── Protected app pages (biggest share of the old bundle) ──────────────────
const Dashboard                = lazy(() => import('./pages/app/Dashboard'));
const ManifestPage             = lazy(() => import('./pages/app/ManifestPage'));
const InstructorSchedulePage   = lazy(() => import('./pages/app/InstructorSchedulePage'));
const CustomersPage            = lazy(() => import('./pages/app/CustomersPage'));
const CustomerDetailPage       = lazy(() => import('./pages/app/CustomerDetailPage'));
const CheckInPage              = lazy(() => import('./pages/app/CheckInPage'));
const SafetyPage               = lazy(() => import('./pages/app/SafetyPage'));
const SafetyDashboard          = lazy(() => import('./pages/app/safety/SafetyDashboard'));
const SafetyIncidents          = lazy(() => import('./pages/app/safety/SafetyIncidents'));
const SafetyIncidentDetail     = lazy(() => import('./pages/app/safety/SafetyIncidentDetail'));
const SafetyCompliance         = lazy(() => import('./pages/app/safety/SafetyCompliance'));
const SafetyAuditLog           = lazy(() => import('./pages/app/safety/SafetyAuditLog'));
const AircraftPage             = lazy(() => import('./pages/app/AircraftPage'));
const StaffPage                = lazy(() => import('./pages/app/StaffPage'));
const FinancePage              = lazy(() => import('./pages/app/FinancePage'));
const ReportsPage              = lazy(() => import('./pages/app/ReportsPage'));
const AdminPage                = lazy(() => import('./pages/app/AdminPage'));
const GroupConsolePage         = lazy(() => import('./pages/app/GroupConsolePage'));
const ProfilePage              = lazy(() => import('./pages/app/ProfilePage'));
const ProfileCompletionPage    = lazy(() => import('./pages/app/ProfileCompletionPage'));
const MyBookingsPage           = lazy(() => import('./pages/app/MyBookingsPage'));
const CustomerItemsPage        = lazy(() => import('./pages/app/CustomerItemsPage'));
const CustomerGiftCardsPage    = lazy(() => import('./pages/app/CustomerGiftCardsPage'));
const BookingsSchedulePage     = lazy(() => import('./pages/app/BookingsSchedulePage'));
const NewBookingPage           = lazy(() => import('./pages/app/NewBookingPage'));
const CheckInRoute             = lazy(() => import('./pages/app/CheckInRoute'));
const CustomerWalletPage       = lazy(() => import('./pages/app/CustomerWalletPage'));
const DocumentsPage            = lazy(() => import('./pages/app/DocumentsPage'));
const DocumentsConfigPage      = lazy(() => import('./pages/app/DocumentsConfigPage'));
const DocumentDetailPage       = lazy(() => import('./pages/app/DocumentDetailPage'));
const FieldPlacementPage       = lazy(() => import('./pages/app/FieldPlacementPage'));
const ManifestConfigPage       = lazy(() => import('./pages/app/ManifestConfigPage'));
const CheckInConfigPage        = lazy(() => import('./pages/app/CheckInConfigPage'));
const MediaPage                = lazy(() => import('./pages/app/MediaPage'));
const MediaConfigPage          = lazy(() => import('./pages/app/MediaConfigPage'));
const SmsConfigPage            = lazy(() => import('./pages/app/SmsConfigPage'));
const CustomerMediaPage        = lazy(() => import('./pages/app/CustomerMediaPage'));
const MyUploadsPage            = lazy(() => import('./pages/app/MyUploadsPage'));
const MyFinancialsPage         = lazy(() => import('./pages/app/MyFinancialsPage'));
const JumperProfilesPage       = lazy(() => import('./pages/app/JumperProfilesPage'));
const BlockTicketsPage         = lazy(() => import('./pages/app/BlockTicketsPage'));
const PilotOperationsPage      = lazy(() => import('./pages/app/PilotOperationsPage'));
const PackingPage              = lazy(() => import('./pages/app/PackingPage'));
const GiftCardPage             = lazy(() => import('./pages/app/GiftCardPage'));
const AnnouncementsPage        = lazy(() => import('./pages/app/AnnouncementsPage'));
const ParachuteManagementPage  = lazy(() => import('./pages/app/ParachuteManagementPage'));
const EquipmentPage            = lazy(() => import('./pages/app/EquipmentPage'));
const CustomerTypeConfigPage   = lazy(() => import('./pages/app/CustomerTypeConfigPage'));
const StaffRoleConfigPage      = lazy(() => import('./pages/app/StaffRoleConfigPage'));
const CustomerConfigPage       = lazy(() => import('./pages/app/CustomerConfigPage'));
const CustomerCredentialsConfigPage = lazy(() => import('./pages/app/CustomerCredentialsConfigPage'));
const CalendarBookingConfigPage = lazy(() => import('./pages/app/CalendarBookingConfigPage'));
const AnalyticsPage            = lazy(() => import('./pages/app/AnalyticsPage'));
const VoiceAgentPage           = lazy(() => import('./pages/app/VoiceAgentPage'));
const AnalyticsOverview        = lazy(() => import('./pages/app/analytics/AnalyticsOverview'));
const AnalyticsStaff           = lazy(() => import('./pages/app/analytics/AnalyticsStaff'));
const AnalyticsAircraft        = lazy(() => import('./pages/app/analytics/AnalyticsAircraft'));
const AnalyticsDocuments       = lazy(() => import('./pages/app/analytics/AnalyticsDocuments'));
const AnalyticsRevenue         = lazy(() => import('./pages/app/analytics/AnalyticsRevenue'));
const AnalyticsSafety          = lazy(() => import('./pages/app/analytics/AnalyticsSafety'));
const AnalyticsCustomers       = lazy(() => import('./pages/app/analytics/AnalyticsCustomers'));
const AnalyticsVideo           = lazy(() => import('./pages/app/analytics/AnalyticsVideo'));
// Small inline fallback while a chunk is loading. Intentionally lightweight —
// no heavy Loader2 icon import here to keep the initial bundle minimal.
const RouteFallback = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#64748b',
      fontSize: 14,
    }}
  >
    Loading…
  </div>
);
// App Pages
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SystemConfigProvider>
        <Router>
          {/* Suspense wraps every lazy-loaded route. Any page not in the
              initial eager bundle shows the fallback briefly while its chunk
              downloads (typically <200ms on a warm cache). */}
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public Routes */}
            {/* The group host has no marketing story of its own — nobody
                lands there to be sold a jump. Whoever opens it is an owner or
                admin choosing a field, so `/` is the sign-in screen, which
                already carries the dropzone chooser.

                `?type=staff` matters: LoginPage defaults to `customer` when
                the param is absent, which would sign an owner into the
                customer view. There is no staff/customer toggle on the form —
                the type comes only from the URL.

                Only the group build. Northshore, southshore, dev and demo
                keep their landing page, and ultimatedzm keeps its marketing
                one (that host serves `/` directly; only its deeper paths are
                redirected away, so bookmarks still land on the real page).

                Deep public paths are untouched — /book, /waiver/*, /sign/*
                and the kiosk still resolve here, which is what keeps already
                sent customer links alive. */}
            <Route path="/" element={isGroupHost() ? <Navigate to="/login?type=staff" replace /> : <LandingPage />} />
            <Route path="/book" element={<BookingPage />} />
            <Route path="/book/event/:slug" element={<BookingPage />} />
            <Route path="/book/:slug" element={<BookingPage />} />
            <Route path="/booking-success" element={<BookingSuccessPage />} />
            <Route path="/group-booking-success" element={<GroupBookingSuccessPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            {/* Group SSO landing. Present on every build, harmless where SSO is
                not configured: the backend answers "not configured" and the
                page tells the visitor to sign in with a password. */}
            <Route path="/sso" element={<SsoHandoffPage />} />
            
            {/* Public Document Signing */}
            <Route path="/sign/:token" element={<SigningPage />} />
            
            {/* Public Group Booking Details */}
            <Route path="/booking/group/:token/details" element={<GroupDetailsPage />} />
            <Route path="/group-booking/:token" element={<GroupBookingDetailsPage />} />
            <Route path='/sell' element={<PublicRoute><CustomerLandingPage /></PublicRoute>} />
            {/* Public Media View (branded landing page) */}
            <Route path="/media/view/:token" element={<MediaViewPage />} />
            {/* Public Customer Gallery (branded delivery gallery — videos + photos) */}
            <Route path="/gallery/:token" element={<CustomerGalleryPage />} />
            {/* Paid-media unlock checkout (Path B paywall). The auto-edit locked
                gallery's CTA points here via CHECKOUT_URL_TEMPLATE —
                /unlock/{job_id}?item={item}. Public: no login in that journey. */}
            <Route path="/unlock/:jobId" element={<MediaUnlockPage />} />

            {/* Live Departure Board */}
            <Route path="/live-board" element={<LiveBoardPage />} />
            
            {/* Kiosk Routes (Public but restricted to kiosk role).
                KioskLayout applies the portrait frame + .kiosk class to
                every descendant — see src/layouts/KioskLayout.js. */}
            <Route path="/kiosk" element={<KioskLayout />}>
              <Route path="login" element={<KioskLoginPage />} />
              <Route path="scanner" element={<KioskScannerPage />} />
              <Route path="payment-success" element={<KioskPaymentSuccess />} />
              <Route path="payment-cancelled" element={<KioskPaymentCancelled />} />
            </Route>
            
            {/* Mobile Waiver Signing (opened from kiosk QR code) */}
            <Route path="/waiver/:bookingId" element={<WaiverMobilePage />} />
            <Route path="/waiver/guest/:customerId" element={<WaiverGuestPage />} />
            
            {/* Protected App Routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={
                <DashboardOrSuperAdmin>
                  <ProtectedRoute module="dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                </DashboardOrSuperAdmin>
              } />
              <Route path="manifest" element={
                <ProtectedRoute module="manifest">
                  <ManifestPage />
                </ProtectedRoute>
              } />
              <Route path="manifest/config" element={
                <ProtectedRoute module="admin">
                  <ManifestConfigPage />
                </ProtectedRoute>
              } />
              <Route path="schedule" element={
                <ProtectedRoute module="schedule">
                  <InstructorSchedulePage />
                </ProtectedRoute>
              } />
              <Route path="customers" element={
                <ProtectedRoute module="customers">
                  <CustomersPage />
                </ProtectedRoute>
              } />
              <Route path="customers/config" element={
                <ProtectedRoute module="admin">
                  <CustomerTypeConfigPage />
                </ProtectedRoute>
              } />
              <Route path="customers/config/:slug/credentials" element={
                <ProtectedRoute module="admin">
                  <CustomerCredentialsConfigPage />
                </ProtectedRoute>
              } />
              <Route path="customer-config" element={
                <ProtectedRoute module="admin">
                  <CustomerConfigPage />
                </ProtectedRoute>
              } />
              <Route path="customers/:id" element={
                <ProtectedRoute module="customers">
                  <CustomerDetailPage />
                </ProtectedRoute>
              } />
              <Route path="check-in" element={
                <ProtectedRoute module="check-in">
                  <CheckInRoute />
                </ProtectedRoute>
              } />
              <Route path="check-in/config" element={
                <ProtectedRoute module="admin">
                  <CheckInConfigPage />
                </ProtectedRoute>
              } />
              {/* Waiver management is now integrated into the Documents page (Waiver Templates tab) */}
              <Route path="safety" element={
                <ProtectedRoute module="safety">
                  <SafetyPage />
                </ProtectedRoute>
              }>
                <Route index element={<SafetyDashboard />} />
                <Route path="dashboard" element={<SafetyDashboard />} />
                <Route path="incidents" element={<SafetyIncidents />} />
                <Route path="incidents/:id" element={<SafetyIncidentDetail />} />
                <Route path="compliance" element={<SafetyCompliance />} />
                <Route path="audit-logs" element={<SafetyAuditLog />} />
              </Route>
              <Route path="aircraft" element={
                <ProtectedRoute module="aircraft">
                  <AircraftPage />
                </ProtectedRoute>
              } />
              <Route path="staff" element={
                <ProtectedRoute module="staff">
                  <StaffPage />
                </ProtectedRoute>
              } />
              <Route path="staff/config" element={
                <ProtectedRoute module="admin">
                  <StaffRoleConfigPage />
                </ProtectedRoute>
              } />
              <Route path="finance" element={
                <ProtectedRoute module="finance">
                  <FinancePage />
                </ProtectedRoute>
              } />
              <Route path="gift-cards" element={
                <ProtectedRoute module="gift-cards">
                  <GiftCardPage />
                </ProtectedRoute>
              } />
              <Route path="reports" element={
                <ProtectedRoute module="reports">
                  <ReportsPage />
                </ProtectedRoute>
              } />
              {/* The group host administers no field of its own, so its Admin
                  screen would configure a dropzone nobody flies at. The route
                  stays mounted and redirects rather than 404-ing, because
                  bookmarks and older emails still point at it. */}
              <Route path="admin" element={
                isGroupHost()
                  ? <Navigate to="/app/group" replace />
                  : <ProtectedRoute module="admin"><AdminPage /></ProtectedRoute>
              } />
              {/* Owner-only, and enforced on the BACKEND by requireOwner — the
                  page renders an explanation on 403 rather than this route
                  guessing at permissions the server actually decides. */}
              <Route path="group/:section?" element={
                <ProtectedRoute>
                  <GroupConsolePage />
                </ProtectedRoute>
              } />
              <Route path="voice-agent" element={
                <ProtectedRoute module="voice-agent">
                  <VoiceAgentPage />
                </ProtectedRoute>
              } />
              {/* Back-compat: the old single "Call Logs" path now lives under the
                  AI Voice Agent dashboard. */}
              <Route path="calls" element={<Navigate to="/app/voice-agent" replace />} />
              <Route path="announcements" element={
                <ProtectedRoute module="manifest">
                  <AnnouncementsPage />
                </ProtectedRoute>
              } />
              <Route path="profile" element={
                <ProtectedRoute module="profile">
                  <ProfilePage />
                </ProtectedRoute>
              } />
              <Route path="profile/complete" element={
                <ProtectedRoute module="profile">
                  <ProfileCompletionPage />
                </ProtectedRoute>
              } />
              {/* My Bookings is customer-only. CustomerRoute redirects
                  non-customer accounts (no linked customerProfile) to the
                  dashboard instead of letting them fire customer-scoped
                  fetches that 4xx and blank the page (Bug 035). Note:
                  `bookings/new` below is deliberately NOT wrapped — staff
                  create bookings there too. */}
              <Route path="bookings" element={
                <ProtectedRoute module="bookings">
                  <CustomerRoute>
                    <MyBookingsPage />
                  </CustomerRoute>
                </ProtectedRoute>
              } />
              {/* Staff weekly/day booking planner. Deliberately NOT wrapped in
                  CustomerRoute — this is the staff-facing bookings calendar,
                  distinct from the customer-only "My Bookings" list above. */}
              <Route path="bookings/schedule" element={
                <ProtectedRoute module="bookings">
                  <BookingsSchedulePage />
                </ProtectedRoute>
              } />
              <Route path="bookings/new" element={
                <ProtectedRoute module="bookings">
                  <NewBookingPage />
                </ProtectedRoute>
              } />
              {/* Dropzone shop — the customer-facing Items (merchandise)
                  catalog. Customer-only like My Bookings: staff sell/see items
                  from the customer profile page, not from their own portal. */}
              <Route path="shop" element={
                <ProtectedRoute module="items">
                  <CustomerRoute>
                    <CustomerItemsPage />
                  </CustomerRoute>
                </ProtectedRoute>
              } />
              <Route path="wallet" element={
                <ProtectedRoute module="wallet">
                  <CustomerWalletPage />
                </ProtectedRoute>
              } />
              {/* Customer gift cards — buy a card (wallet or Clover) and
                  email it to someone. Customer-only like the shop: staff
                  issue/manage cards from /app/gift-cards. */}
              <Route path="my-gift-cards" element={
                <ProtectedRoute module="my-gift-cards">
                  <CustomerRoute>
                    <CustomerGiftCardsPage />
                  </CustomerRoute>
                </ProtectedRoute>
              } />
              <Route path="documents" element={
                <ProtectedRoute module="documents">
                  <DocumentsPage />
                </ProtectedRoute>
              } />
              {/* Documents → Config page (own dedicated route, matches the
                  /app/<module>/config pattern used by Media/Manifest/etc.) */}
              <Route path="documents/config" element={
                <ProtectedRoute module="admin">
                  <DocumentsConfigPage />
                </ProtectedRoute>
              } />
              {/* Legacy redirect: the old Doc Automation page now lives at
                  /documents/config. Placed before /documents/:id so the
                  literal "automation" segment wins over the dynamic :id. */}
              <Route path="documents/automation" element={<Navigate to="/app/documents/config" replace />} />
              {/* Template-edit route — placed BEFORE the dynamic
                  /documents/:id/fields so the literal "templates" segment
                  wins route specificity in both React Router v6 and the
                  read order. */}
              <Route path="documents/templates/:id/fields" element={
                <ProtectedRoute module="admin">
                  <FieldPlacementPage />
                </ProtectedRoute>
              } />
              <Route path="documents/:id" element={
                <ProtectedRoute module="documents">
                  <DocumentDetailPage />
                </ProtectedRoute>
              } />
              <Route path="documents/:id/fields" element={
                <ProtectedRoute module="documents">
                  <FieldPlacementPage />
                </ProtectedRoute>
              } />
              <Route path="media" element={
                <ProtectedRoute module="media">
                  <MediaPage />
                </ProtectedRoute>
              } />
              <Route path="media/config" element={
                <ProtectedRoute module="admin">
                  <MediaConfigPage />
                </ProtectedRoute>
              } />
              <Route path="jumper-profiles" element={
                <ProtectedRoute module="admin">
                  <JumperProfilesPage />
                </ProtectedRoute>
              } />
              <Route path="block-tickets" element={
                <ProtectedRoute module="admin">
                  <BlockTicketsPage />
                </ProtectedRoute>
              } />
              <Route path="parachutes" element={
                <ProtectedRoute module="admin">
                  <ParachuteManagementPage />
                </ProtectedRoute>
              } />
              <Route path="equipment" element={
                <ProtectedRoute module="admin">
                  <EquipmentPage />
                </ProtectedRoute>
              } />
                
              <Route path="media/analytics" element={
                <ProtectedRoute module="media">
                  <AnalyticsPage />
                </ProtectedRoute>
              }>
                <Route index element={<AnalyticsVideo />} />
              </Route>
              <Route path="analytics" element={
                <ProtectedRoute module="dashboard">
                  <AnalyticsPage />
                </ProtectedRoute>
              }>
                <Route index element={<AnalyticsOverview />} />
                <Route path="overview" element={<AnalyticsOverview />} />
                <Route path="staff" element={<AnalyticsStaff />} />
                <Route path="aircraft" element={<AnalyticsAircraft />} />
                <Route path="documents" element={<AnalyticsDocuments />} />
                <Route path="revenue" element={<AnalyticsRevenue />} />
                <Route path="safety" element={<AnalyticsSafety />} />
                <Route path="customers" element={<AnalyticsCustomers />} />
                <Route path="video" element={<AnalyticsVideo />} />
              </Route>
              <Route path="my-uploads" element={
                <ProtectedRoute module="media">
                  <MyUploadsPage />
                </ProtectedRoute>
              } />
              <Route path="my-financials" element={<MyFinancialsPage />} />
              <Route path="my-media" element={
                <ProtectedRoute module="my-media">
                  <CustomerMediaPage />
                </ProtectedRoute>
              } />
              <Route path="fun-jumper" element={
                <ProtectedRoute module="fun-jumper">
                  <FunJumperPage />
                </ProtectedRoute>
              } />
              <Route path="pilot-ops" element={
                <ProtectedRoute module="admin">
                  <PilotOperationsPage />
                </ProtectedRoute>
              } />
              <Route path="packing" element={
                <ProtectedRoute module="packing">
                  <PackingPage />
                </ProtectedRoute>
              } />
              <Route path="calendar-config" element={
                <ProtectedRoute module="admin">
                  <CalendarBookingConfigPage />
                </ProtectedRoute>
              } />
              <Route path="sms-config" element={
                <ProtectedRoute module="admin">
                  <SmsConfigPage />
                </ProtectedRoute>
              } />
            </Route>
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </Router>
        </SystemConfigProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
